import { useDisclosure } from "@chakra-ui/react";
import { useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import "../styles/shopnow.css";
import { ProfileContext } from "../store/profile_context";
import { GlobalContext } from "../store/global_context";
import {
  GetTodayLikeCount,
  UpdateUserLocation,
  GetUserRecommendations,
  AddDislike,
  ClearMyDislikes,
  RECOMMENDATIONS_PAGE_SIZE,
} from "../services/backend";
import { AuthContext } from "../store/auth-context";
import { NoRecommendationsPage } from "./chadcdn/no-recommendations-page";
import HomeLikesSection from "./HomeLikesSection";
import ProfilesLoadingRadar from "./ProfilesLoadingRadar";
import ExploreProfileView from "./ExploreProfileView";
import PaywallModal from "./PaywallModal";
import { filterExploreProfiles } from "../util/profileQuality";
import { detectUserLocation } from "../util/detectCountry";
import { getRecommendationsSeed } from "../util/recommendationsSeed";
import { MOBILE_SHELL_HEIGHT } from "../constants/mobileApp";
import { refreshUserAfterCheckout, usePremiumAccess } from "../hooks/usePremiumAccess";

const PAGE_SIZE = RECOMMENDATIONS_PAGE_SIZE || 40;
/** Prefetch when the remaining queue drops below this. */
const QUEUE_BUFFER_TARGET = 15;

function profileId(entry) {
  return entry?.user?.id ?? entry?.id ?? null;
}

function dedupeAppend(existing, incoming, excludeIds) {
  const seen = new Set([
    ...existing.map(profileId).filter(Boolean),
    ...(excludeIds || []),
  ]);
  const next = [...existing];
  for (const item of incoming) {
    const id = profileId(item);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    next.push(item);
  }
  return next;
}

const ShopNow = () => {
  const profileCtx = useContext(ProfileContext);
  const globalCtx = useContext(GlobalContext);
  const authCtx = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPremium, refreshPremiumStatus } = usePremiumAccess();
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [authCtx.authenticatedUser]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [profileQueue, setProfileQueue] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isRestartingPool, setIsRestartingPool] = useState(false);

  const fetchingMoreRef = useRef(false);
  const restartingRef = useRef(false);
  const seedKeyRef = useRef("");
  const hasMoreRef = useRef(true);
  const queueLenRef = useRef(0);
  const profileQueueRef = useRef([]);
  /** Profiles already passed this round (liked/skipped). */
  const seenIdsRef = useRef(new Set());
  /** Broken-photo IDs — keep across restarts so we don't loop on them forever. */
  const brokenPhotoIdsRef = useRef(new Set());
  /** Full pass history so we can loop instantly if needed. */
  const roundArchiveRef = useRef([]);
  /** After a clear-dislikes restart still returns nobody usable, stop looping. */
  const allowAutoRestartRef = useRef(true);

  const profiles = useMemo(
    () => {
      const filtered = filterExploreProfiles(
        Array.isArray(profileCtx.recommendedUsers) ? profileCtx.recommendedUsers : [],
        authCtx.authenticatedUser?.gender ?? authCtx.authenticatedUser?.preferences?.gender
      );
      return filtered.filter((entry) => {
        const id = profileId(entry);
        return id && !brokenPhotoIdsRef.current.has(id);
      });
    },
    [
      profileCtx.recommendedUsers,
      authCtx.authenticatedUser?.gender,
      authCtx.authenticatedUser?.preferences?.gender,
    ]
  );

  const profileIdsKey = useMemo(
    () => profiles.map((p) => p.user?.id).filter(Boolean).join("|"),
    [profiles]
  );

  // Seed queue from first page; reset when recommendations refresh.
  useEffect(() => {
    if (profileCtx.recommendedUsers == null) return;
    if (seedKeyRef.current === profileIdsKey) return;
    seedKeyRef.current = profileIdsKey;
    seenIdsRef.current = new Set();
    roundArchiveRef.current = [];
    allowAutoRestartRef.current = true;
    setProfileQueue(profiles);
    profileQueueRef.current = profiles;
    queueLenRef.current = profiles.length;
    hasMoreRef.current = true;
    setHasMore(true);
    fetchingMoreRef.current = false;
    restartingRef.current = false;
    setIsRestartingPool(false);
  }, [profileIdsKey, profiles, profileCtx.recommendedUsers]);

  useEffect(() => {
    queueLenRef.current = profileQueue.length;
    profileQueueRef.current = profileQueue;
  }, [profileQueue]);

  const currentProfile = profileQueue[0] ?? null;

  const recordPass = useCallback(
    (skippedId) => {
      if (!user?.id || !skippedId) return;
      void AddDislike({
        dislikerProfileId: user.id,
        dislikedProfileId: skippedId,
      }).catch((error) => {
        console.error("Failed to record skip:", error);
      });
    },
    [user?.id]
  );

  const fetchMoreProfiles = useCallback(async () => {
    if (!user?.id || !hasMoreRef.current || fetchingMoreRef.current) return;
    fetchingMoreRef.current = true;
    try {
      // Keep pulling while the RPC still has a full page. Client filtering used to
      // stop Discover early (added === 0) even when more matching profiles existed.
      let rounds = 0;
      while (
        rounds < 8 &&
        hasMoreRef.current &&
        profileQueueRef.current.length < QUEUE_BUFFER_TARGET
      ) {
        rounds += 1;
        const page = await GetUserRecommendations(user.id, {
          limit: PAGE_SIZE,
          offset: 0,
          seed: getRecommendationsSeed(user.id),
        });
        const rejectedIds = Array.isArray(page?.rejectedIds) ? page.rejectedIds : [];
        for (const id of rejectedIds) {
          if (!id || brokenPhotoIdsRef.current.has(id)) continue;
          brokenPhotoIdsRef.current.add(id);
          recordPass(id);
        }

        const items = (Array.isArray(page?.items) ? page.items : []).filter((entry) => {
          const id = profileId(entry);
          return id && !brokenPhotoIdsRef.current.has(id);
        });
        const prev = profileQueueRef.current;
        const next = dedupeAppend(prev, items, seenIdsRef.current);
        const added = next.length - prev.length;
        profileQueueRef.current = next;
        queueLenRef.current = next.length;
        setProfileQueue(next);

        const rpcHasMore =
          page?.hasMore === true || (page?.fetchedCount ?? 0) >= PAGE_SIZE;
        if (!rpcHasMore) {
          hasMoreRef.current = false;
          setHasMore(false);
          break;
        }

        // Full RPC page already represented in queue/seen, and nothing new to
        // exclude — wait until the user acts (dislike/like) before asking again.
        if (added === 0 && rejectedIds.length === 0) break;
      }
    } catch (error) {
      console.error("Failed to prefetch recommendations:", error);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      fetchingMoreRef.current = false;
    }
  }, [user?.id, recordPass]);

  const restartDiscoverRound = useCallback(async () => {
    if (!user?.id || restartingRef.current) return;
    restartingRef.current = true;
    setIsRestartingPool(true);
    try {
      // Clear passes so the same matching pool can be shown again.
      // Likes/matches stay excluded. Broken-photo IDs stay excluded client-side.
      await ClearMyDislikes(user.id);
      seenIdsRef.current = new Set();
      hasMoreRef.current = true;
      setHasMore(true);
      allowAutoRestartRef.current = true;

      const page = await GetUserRecommendations(user.id, {
        limit: PAGE_SIZE,
        offset: 0,
        seed: getRecommendationsSeed(user.id),
      });
      const rejectedIds = Array.isArray(page?.rejectedIds) ? page.rejectedIds : [];
      for (const id of rejectedIds) {
        if (!id) continue;
        brokenPhotoIdsRef.current.add(id);
        recordPass(id);
      }
      let items = Array.isArray(page?.items) ? page.items : [];
      items = items.filter((entry) => {
        const id = profileId(entry);
        return id && !brokenPhotoIdsRef.current.has(id) && !seenIdsRef.current.has(id);
      });
      if (!items.length && roundArchiveRef.current.length) {
        items = roundArchiveRef.current.filter((entry) => {
          const id = profileId(entry);
          return id && !brokenPhotoIdsRef.current.has(id);
        });
      }
      roundArchiveRef.current = [];
      setProfileQueue(items);
      profileQueueRef.current = items;
      queueLenRef.current = items.length;
      if (!items.length) {
        hasMoreRef.current = false;
        setHasMore(false);
        allowAutoRestartRef.current = false;
      } else {
        // Keep filling from the rest of the opposite-gender pool.
        hasMoreRef.current = page?.hasMore !== false;
        setHasMore(page?.hasMore !== false);
      }
    } catch (error) {
      console.error("Failed to restart Discover pool:", error);
      if (roundArchiveRef.current.length) {
        const items = roundArchiveRef.current.filter((entry) => {
          const id = profileId(entry);
          return id && !brokenPhotoIdsRef.current.has(id);
        });
        roundArchiveRef.current = [];
        seenIdsRef.current = new Set();
        setProfileQueue(items);
        profileQueueRef.current = items;
        queueLenRef.current = items.length;
        if (items.length) {
          hasMoreRef.current = true;
          setHasMore(true);
        } else {
          allowAutoRestartRef.current = false;
          hasMoreRef.current = false;
          setHasMore(false);
        }
      } else {
        allowAutoRestartRef.current = false;
        hasMoreRef.current = false;
        setHasMore(false);
      }
    } finally {
      restartingRef.current = false;
      setIsRestartingPool(false);
    }
  }, [user?.id, recordPass]);

  // Load more when the queue runs low (after likes / next).
  useEffect(() => {
    if (profileCtx.recommendedUsers == null) return;
    if (!hasMore) return;
    if (profileQueue.length >= QUEUE_BUFFER_TARGET) return;
    fetchMoreProfiles();
  }, [profileQueue.length, hasMore, fetchMoreProfiles, profileCtx.recommendedUsers]);

  // Everyone seen → clear passes and start the pool over again.
  useEffect(() => {
    if (profileCtx.recommendedUsers == null) return;
    if (profileQueue.length > 0) return;
    if (hasMore) return;
    if (restartingRef.current) return;
    if (!allowAutoRestartRef.current) return;
    void restartDiscoverRound();
  }, [profileQueue.length, hasMore, profileCtx.recommendedUsers, restartDiscoverRound]);

  const removeCurrentProfile = useCallback(() => {
    const prev = profileQueueRef.current;
    if (!prev.length) return;
    const current = prev[0];
    const id = profileId(current);
    if (id) seenIdsRef.current.add(id);
    if (current) {
      roundArchiveRef.current = dedupeAppend(roundArchiveRef.current, [current], []);
    }
    const next = prev.slice(1);
    profileQueueRef.current = next;
    queueLenRef.current = next.length;
    setProfileQueue(next);
    if (hasMoreRef.current && !fetchingMoreRef.current) {
      void fetchMoreProfiles();
    }
  }, [fetchMoreProfiles]);

  const skipProfile = useCallback(() => {
    const skippedId = profileId(profileQueueRef.current[0]);
    removeCurrentProfile();
    // Persist pass so Discover walks the full opposite-sex pool before looping.
    recordPass(skippedId);
  }, [removeCurrentProfile, recordPass]);

  const advanceAfterLike = removeCurrentProfile;

  const advanceUnusableProfile = useCallback(() => {
    const skippedId = profileId(profileQueueRef.current[0]);
    if (skippedId) brokenPhotoIdsRef.current.add(skippedId);
    removeCurrentProfile();
    // Also persist pass for this round; restart keeps broken IDs excluded.
    recordPass(skippedId);
  }, [removeCurrentProfile, recordPass]);

  useEffect(() => {
    const checkoutState = searchParams.get("checkout");
    if (checkoutState !== "success" || !authCtx.authenticatedUser?.id) return;

    let cancelled = false;

    async function syncAfterCheckout() {
      await refreshUserAfterCheckout(authCtx.authenticatedUser.id, authCtx.authenticateUser);
      if (!cancelled) {
        await refreshPremiumStatus();
        searchParams.delete("checkout");
        setSearchParams(searchParams, { replace: true });
      }
    }

    syncAfterCheckout();
    return () => {
      cancelled = true;
    };
  }, [
    authCtx.authenticatedUser?.id,
    authCtx.authenticateUser,
    refreshPremiumStatus,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    async function UpdateLocation() {
      if (!user?.id) return;

      try {
        const location = await detectUserLocation();
        if (!location.lat || !location.long) return;

        await UpdateUserLocation({
          userId: user.id,
          longitude: location.long,
          latitude: location.lat,
          country: location.country || undefined,
        });
      } catch (error) {
        console.error("Failed to update location:", error);
      }
    }
    UpdateLocation();
  }, [user?.id]);

  useEffect(() => {
    async function GetAllProfiles() {
      if (!user?.id) return;
      profileCtx.GetAllProfiles(user.id);
    }
    GetAllProfiles();
  }, [profileCtx.GetAllProfiles, user?.id]);

  useEffect(() => {
    async function GetDbLikesCount() {
      if (!user?.id) return;
      try {
        const dbLikesCount = await GetTodayLikeCount(user.id);
        globalCtx.updateLikeCount(dbLikesCount);
      } catch (error) {
        console.error("Failed to fetch like count:", error);
      }
    }
    GetDbLikesCount();
  }, [user?.id]);

  const profilesPanel = (
    <>
      {profileCtx.recommendedUsers == null || isRestartingPool ? (
        <Box w="100%" flex={{ base: 1, lg: "unset" }} minH={{ base: "240px", lg: "auto" }} display="flex">
          <ProfilesLoadingRadar minH={{ base: "100%", lg: "56vh" }} />
        </Box>
      ) : profileQueue.length > 0 && currentProfile ? (
        <ExploreProfileView
          profile={currentProfile}
          onSkip={skipProfile}
          onLikeAdvance={advanceAfterLike}
          onUnusableProfile={advanceUnusableProfile}
          openPaywall={onOpen}
          stripeSubscriptionStatus={isPremium === true}
          onBlocked={(blockedId) => {
            if (!blockedId) return;
            seenIdsRef.current.add(blockedId);
            recordPass?.(blockedId);
          }}
        />
      ) : hasMore ? (
        <Box w="100%" flex={{ base: 1, lg: "unset" }} minH={{ base: "240px", lg: "auto" }} display="flex">
          <ProfilesLoadingRadar minH={{ base: "100%", lg: "56vh" }} />
        </Box>
      ) : (
        <NoRecommendationsPage onRefresh={() => void restartDiscoverRound()} />
      )}
    </>
  );

  return (
    <>
      <Box
        w="100%"
        display={{ base: "flex", lg: "block" }}
        flexDirection="column"
        minH={{ base: MOBILE_SHELL_HEIGHT, lg: "auto" }}
        py={{ base: 0, md: 4 }}
      >
        <Box flexShrink={0}>
          <HomeLikesSection onUpgrade={onOpen} />
        </Box>
        <Box flex={{ base: 1, lg: "unset" }} minH={0} display="flex" flexDirection="column">
          {profilesPanel}
        </Box>
      </Box>
      <PaywallModal isOpen={isOpen} onClose={onClose} userId={user?.id} />
    </>
  );
};

export default ShopNow;
